import { useState, useEffect, useCallback } from "react";
import { db } from "../config/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";

export type BuildStatus = "idle" | "queued" | "building" | "complete" | "failed";

interface UseBuildStatusResult {
  buildStatus: BuildStatus;
  buildUrl: string | null;
  setBuildQueued: () => Promise<void>;
  isPolling: boolean;
}

const POLL_INTERVAL_MS = 30_000;

/**
 * Manages build pipeline status for a project.
 *
 * Reads `buildStatus` and `buildUrl` from the project Firestore doc.
 * Polls every 30 seconds while status is "queued" or "building".
 * `setBuildQueued` writes "queued" to Firestore (called after Build It export).
 */
export function useBuildStatus(userID: string, projectID: string): UseBuildStatusResult {
  const [buildStatus, setBuildStatus] = useState<BuildStatus>("idle");
  const [buildUrl, setBuildUrl] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const fetchBuildStatus = useCallback(async () => {
    if (!userID || !projectID) return;
    try {
      const projectRef = doc(db, "users", userID, "projects", projectID);
      const snap = await getDoc(projectRef);
      const data = snap.data();
      if (data?.buildStatus) {
        setBuildStatus(data.buildStatus as BuildStatus);
      }
      if (data?.buildUrl) {
        setBuildUrl(data.buildUrl as string);
      }
    } catch {
      // Non-critical — don't surface polling errors to the user
    }
  }, [userID, projectID]);

  const setBuildQueued = useCallback(async () => {
    if (!userID || !projectID) return;
    const projectRef = doc(db, "users", userID, "projects", projectID);
    await updateDoc(projectRef, { buildStatus: "queued" });
    setBuildStatus("queued");
  }, [userID, projectID]);

  // Initial fetch on mount
  useEffect(() => {
    fetchBuildStatus();
  }, [fetchBuildStatus]);

  // Poll while pipeline is running
  useEffect(() => {
    const isActive = buildStatus === "queued" || buildStatus === "building";
    setIsPolling(isActive);
    if (!isActive) return;

    const interval = setInterval(fetchBuildStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [buildStatus, fetchBuildStatus]);

  return { buildStatus, buildUrl, setBuildQueued, isPolling };
}
